import axios from 'axios';
import { API_BASE } from './config';

// GET all restaurants for a property
export const fetchRestaurants = (propertyId) =>
  axios.get(`${API_BASE}/restaurants`, { params: { propertyId } });

// GET single restaurant
export const fetchSingleRestaurant = (id, propertyId) =>
  axios.get(`${API_BASE}/restaurants/${id}`, { params: { propertyId } });

// CREATE new restaurant
export const createRestaurant = (data) =>
  axios.post(`${API_BASE}/restaurants`, data);

// UPDATE restaurant
export const updateRestaurant = (id, data) =>
  axios.put(`${API_BASE}/restaurants/${id}`, data);

// DELETE restaurant (soft delete)
export const deleteRestaurant = (id, propertyId) =>
  axios.delete(`${API_BASE}/restaurants/${id}`, { params: { propertyId } });

// RESTORE deleted restaurant
export const restoreRestaurant = (id, propertyId) =>
  axios.post(`${API_BASE}/restaurants/${id}/restore`, { propertyId });

// UPLOAD image
export const uploadRestaurantImage = ({ file, propertyId }) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('propertyId', propertyId);

  console.log('📤 Uploading image with:', { file, propertyId });

  return axios.post(`${API_BASE}/restaurants/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-property-id': propertyId
    }
  });
};

// PATCH a specific menu item (e.g. to attach image)
export const updateMenuItem = (restaurantId, itemId, data, propertyId) => {
  if (!propertyId && !data?.propertyId) {
    console.warn('⚠️ Warning: propertyId is missing in both arguments and data. This may cause backend rejection.', {
      restaurantId,
      itemId,
      data
    });
  }

  if (data.promotion && typeof data.promotion === 'object' && data.promotion._id) {
    data.promotion = data.promotion._id;
  }

  const payload = {
    ...data,
    propertyId: propertyId ?? data.propertyId
  };

  console.log('🛠 updateMenuItem debug:', {
    restaurantId,
    itemId,
    propertyId,
    payload,
    url: `${API_BASE}/restaurants/${restaurantId}/menu/${itemId}`
  });

  return axios.patch(`${API_BASE}/restaurants/${restaurantId}/menu/${itemId}`, payload);
};

export const fetchMenuItem = (restaurantId, itemId) =>
  axios.get(`${API_BASE}/restaurants/${restaurantId}/menu/${itemId}`);

// DELETE menu image
export const deleteMenuItemImage = (restaurantId, itemId, propertyId) =>
  axios.delete(`${API_BASE}/restaurants/${restaurantId}/menu/${itemId}/image`, {
    params: { propertyId }
  });

// GET all menu items for a restaurant
export const fetchMenuItems = (restaurantId, propertyId) => {
  console.log('📥 fetchMenuItems called with:', { restaurantId, propertyId });
  return axios.get(`${API_BASE}/restaurants/${restaurantId}/menu`, {
    params: { propertyId }
  });
}

// GET all promotions for a specific restaurant and property
export const fetchPromotions = (restaurantId, propertyId) => {
  console.log('📥 fetchPromotions called with:', { restaurantId, propertyId });
  return axios.get(`${API_BASE}/restaurant-promotions`, {
    params: { restaurantId, propertyId }
  });
};

// CREATE new promotion
export const createPromotion = (data, restaurantId, propertyId) => {
  const payload = { ...data, restaurantId, propertyId };
  return axios.post(`${API_BASE}/restaurant-promotions`, payload);
};

// UPDATE promotion
export const updatePromotion = (id, data, restaurantId, propertyId) => {
  const payload = { ...data, restaurantId, propertyId };
  return axios.patch(`${API_BASE}/restaurant-promotions/${id}`, payload);
};

// DELETE promotion
export const deletePromotion = (id, propertyId, restaurantId) =>
  axios.delete(`${API_BASE}/restaurant-promotions/${id}`, {
    params: { propertyId, restaurantId }
  });

export async function reorderRestaurantMenu(restaurantId, propertyId, menu) {
  const res = await fetch(`${API_BASE}/restaurants/${restaurantId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ propertyId, menu })
  });
  if (!res.ok) throw new Error('Failed to reorder restaurant menu');
  return await res.json();
}

export async function reorderRestaurantMenuItems(restaurantId, propertyId, menu) {
  const res = await fetch(`${API_BASE}/restaurants/reorder-menu`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restaurantId, propertyId, menu }),
  });
  if (!res.ok) throw new Error('Failed to reorder restaurant menu');
  return await res.json();
}

// CREATE new menu item
export const createMenuItem = (restaurantId, data, propertyId) => {
  const payload = { ...data, propertyId: propertyId ?? data?.propertyId };
  if (!payload.propertyId) {
    console.warn('⚠️ createMenuItem called without propertyId. Backend will reject this request.', {
      restaurantId,
      data
    });
  }
  return axios.post(`${API_BASE}/restaurants/${restaurantId}/menu`, payload);
};

// DELETE a menu item
export const deleteMenuItem = (restaurantId, itemId, propertyId) => {
  return axios.delete(`${API_BASE}/restaurants/${restaurantId}/menu/${itemId}`, {
    data: { propertyId }
  });
};

// ---------- Option Templates ----------
export const fetchOptionTemplates = (propertyId) =>
  axios.get(`${API_BASE}/restaurant-option-templates`, { params: { propertyId } });

export const createOptionTemplate = (data) =>
  axios.post(`${API_BASE}/restaurant-option-templates`, data);

export const updateOptionTemplate = (id, data) =>
  axios.patch(`${API_BASE}/restaurant-option-templates/${id}`, data);

export const deleteOptionTemplate = (id, propertyId) =>
axios.delete(`${API_BASE}/restaurant-option-templates/${id}`, { params: { propertyId } });

// ---------- Menu Item Types ----------
export const fetchMenuTypes = (propertyId) =>
  axios.get(`${API_BASE}/restaurant-menu-types`, { params: { propertyId } });

export const createMenuType = (data) =>
  axios.post(`${API_BASE}/restaurant-menu-types`, data);